<template>
<div class="form-step-item step-3">
    <div v-if="baseInfo">
        <div class="formulation-form-row">
            <div class="col-8 print-full">
                <div class="panel title">
                    <h3 class="no-print">Create formulation</h3>
                    <h4>Formulation Detailed Description</h4>
                    <div class="formulation-form-row">
                        <div class="form-group col-8">
                            <input type="text" class="form-control" :disabled="viewMode" v-model="formData.bag_details">
                            <span class="form-error" v-if="$v.formData.bag_details.$dirty && !$v.formData.bag_details.required">Required field</span>
                        </div>
                    </div>
                    <template v-if="formData.use==='patient'">
                        <h4>Patient Details</h4>
                        <div class="formulation-form-row">
                            <div class="form-group col-4">
                                <label for="patient_initials">Initials (optional)</label>
                                <input type="text" :disabled="viewMode" class="form-control" v-model="formData.patient_initials">
                            </div>
                            <div class="form-group col-8">
                                <label for="patient_ref">Hospital or HPN Formulation Reference</label>
                                <input type="text" :disabled="viewMode" class="form-control" v-model="formData.patient_ref">
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="col-4 no-print">
                <SavedPanel :formData="formData" />
                <div class="action-icons no-print">
                    <div class="list">
                        <button><i class="icon-copy" @click.prevent="onModalCopyFormulationOpen()"></i></button>
                        <button><i class="icon-printer" @click.prevent="onPrint()"></i></button>
                        <button v-if="!viewMode && this.formData.status === 'draft'"><i class="icon-delete" @click.prevent="onModalDeleteDraftOpen()"></i></button>
                    </div>
                </div>
            </div>
        </div>
        <div class="formulation-form-row">
            <div class="col-8 print-full">
                <div class="create-formulation-wrapper">
                    <div class="inner">
                        <div class="section-heading">
                            <div class="formulation-form-row">
                                <div class="col-7 d-flex align-items-center">
                                    <label for="bag_volume">Bag Volume</label>
                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <span class="form-bag-volume">{{baseInfo.bag_volume}} mL</span>
                                        <button v-if="!viewMode" class="trigger-change-bag bax-btn bax-btn-blue-brand no-print" @click.prevent="onModalChangeBagOpen()">change</button>
                                    </template>
                                    <template v-else>
                                        <input type="number" :disabled="viewMode" min="0" step="any" class="form-control" v-model="formData.bag_volume" @input="resetStability()" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                        <span class="form-uom">mL</span>
                                    </template>
                                </div>
                                <div class="col-5 d-flex align-items-center">
                                    <h4>Base: {{baseInfo.label}}</h4>
                                </div>
                            </div>
                            <div class="formulation-form-row" v-if="formData.errors.bag || ($v.formData.bag_volume.$dirty && !$v.formData.bag_volume.isValid)">
                                <div class="col-7">
                                    <span class="form-error" v-if="$v.formData.bag_volume.$dirty && !$v.formData.bag_volume.isValid">Required field</span>
                                    <span class="form-error" v-if="formData.errors.bag">{{formData.errors.bag}} Please <router-link :to="{ name: 'TPNCustomFormulationContact' }">contact us</router-link> if you would like to discuss options.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Macronutrients -->
                    <div class="inner" v-if="baseInfo.macronutrients">
                        <div class="section-macronutrients">
                            <h4>Macronutrients</h4>
                            <!-- Amino acid -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.amino_acid">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Amino acid</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <select :disabled="viewMode" class="form-control" v-if="baseInfo.macronutrients.amino_acid.choices.length > 1" v-model="formData.formulation.macronutrients.amino_acid.label" @change="onSelectAminoAcidType($event)">
                                            <option class="d-none" value="">- Select -</option>
                                            <option value="none">Not Required</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.amino_acid.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.amino_acid.choices.length === 1">{{formData.selectedAminoAcidType.label}}</p>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedAminoAcidType">
                                        <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.macronutrients.amino_acid.quantity" @input="onInputAminoacidValue($event)" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                        <span class="form-uom">g</span>
                                    </div>
                                    <div class="col-5" v-if="formData.formulation.macronutrients.amino_acid.nitrogen">
                                        <p>Nitrogen {{formatNumber(formData.formulation.macronutrients.amino_acid.nitrogen, 2) }} {{formData.selectedAminoAcidType.nitrogen_unit}}</p>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.aminoacid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="$v.formData.formulation.macronutrients.amino_acid.quantity.$dirty && !$v.formData.formulation.macronutrients.amino_acid.quantity.isValid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Required field</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Glucose -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.glucose">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Glucose</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4">
                                        <select :disabled="viewMode" class="form-control" v-if="baseInfo.macronutrients.glucose.choices.length > 1" v-model="formData.formulation.macronutrients.glucose.label" @change="onSelectGlucoseType($event)">
                                            <option class="d-none" value="">- Select -</option>
                                            <option value="none">Not Required</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.glucose.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.glucose.choices.length === 1">{{formData.selectedGlucoseType.label}}</p>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedGlucoseType">
                                        <div>
                                            <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.macronutrients.glucose.quantity_g" @input="onInputGlucoseValue($event,'g')" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                            <span class="form-uom">g</span>
                                        </div>
                                        <div>
                                            <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.macronutrients.glucose.quantity_kcal" @input="onInputGlucoseValue($event,'kcal')" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                            <span class="form-uom">kcal</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="$v.formData.formulation.macronutrients.glucose.quantity_g.$dirty && !$v.formData.formulation.macronutrients.glucose.quantity_g.isValid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Required field</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.glucose">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Lipid -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.lipid">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Lipid</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4">
                                        <select :disabled="viewMode" class="form-control" v-if="baseInfo.macronutrients.lipid.choices.length > 1" v-model="formData.formulation.macronutrients.lipid.label" @change="onSelectLipidType($event)">
                                            <option class="d-none" value="">- Select -</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.lipid.choices" :value="type" v-bind:key="typeIndex">
                                                {{ getLipidLabel(type) }}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.lipid.choices.length === 1">{{ getLipidLabel(formData.selectedLipidType.label) }}</p>
                                        <span class="form-error" v-if="$v.formData.formulation.macronutrients.lipid.label.$dirty && !$v.formData.formulation.macronutrients.lipid.label.required">Required field</span>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedLipidType">
                                        <div>
                                            <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.macronutrients.lipid.quantity_g" @input="onInputLipidValue($event,'g')" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                            <span class="form-uom">g</span>
                                        </div>
                                        <div>
                                            <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.macronutrients.lipid.quantity_kcal" @input="onInputLipidValue($event,'kcal')" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                            <span class="form-uom">kcal</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="$v.formData.formulation.macronutrients.lipid.quantity_g.$dirty && !$v.formData.formulation.macronutrients.lipid.quantity_g.isValid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Required field</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.lipids.default">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">{{formData.errors.lipids.default}}</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.lipids.noVitalipid">
                                    <div class="col-12 error-col">
                                        <span class="form-error general">If you would like to add Omegaven to this formulation, please <router-link :to="{ name: 'TPNCustomFormulationContact' }">contact us</router-link></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="inner" v-if="baseInfo.electrolytes.items">
                        <div class="section-electrolytes">
                            <!-- Electrolytes list -->
                            <div class="subpanel">
                                <div class="formulation-form-row">
                                    <div class="col-4 d-flex align-items-center">
                                        <h4>Electrolytes</h4>
                                    </div>

                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <div class="col-3 d-flex align-items-center">
                                            <p>Base Quantity</p>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="col-1 d-flex align-items-center">
                                        </div>
                                    </template>

                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <div class="col-5 d-flex align-items-center">
                                            <p>Extra to be added</p>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="col-3 d-flex align-items-center">
                                            <p v-if="baseInfo.label === 'Primene'">Total per litre</p>
                                            <p v-else>Add to bag</p>
                                        </div>
                                    </template>

                                </div>
                                <template v-for="(item, ItemIndex) in baseInfo.electrolytes.items">
                                    <div class="formulation-form-row" :class="{ 'mt-2': ItemIndex !== 0 }" :key="'data_' + ItemIndex" >
                                        <template v-if="item.choices">
                                            <template v-if="item.choices.length > 1">
                                                <div class="col-12">
                                                    <p>{{item.label}}</p>
                                                </div>
                                                <div class="col-4" :class="{ 'd-flex align-items-center': baseInfo.label !== 'Primene' }">
                                                    <select class="form-control" :disabled="viewMode" v-model="formData.formulation.electrolytes[ItemIndex].type" @change="onSelectElectrolyteType($event, ItemIndex)">
                                                        <option class="d-none" value="">- Select -</option>
                                                        <!-- <option value="none">Not Required</option> -->
                                                        <option v-for="(type, typeIndex) in item.choices" :value="type.label" v-bind:key="typeIndex">
                                                            {{type.label}}
                                                        </option>
                                                    </select>
                                                    <span class="form-error" v-if="$v.formData.formulation.electrolytes.$each[ItemIndex].type.$dirty && !$v.formData.formulation.electrolytes.$each[ItemIndex].type.required">Required field</span>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div class="col-4 d-flex align-items-center">
                                                    <p v-if="baseInfo.label === 'Synthamin + Lipid'">{{item.label}}<br>({{item.choices[0].label}}, excl. lipid)</p>
                                                    <p v-else>{{item.label}} ({{item.choices[0].label}})</p>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <div class="col-4 d-flex align-items-center">
                                                <template v-if="baseInfo.label === 'Primene' && item.label === 'Sodium' && hasOrganicPhosphate()">
                                                    <p>{{item.label}} (excl. Phosphate)</p>
                                                </template>
                                                <template v-else>
                                                <p>{{item.label}}</p>
                                                </template>
                                            </div>
                                        </template>

                                        <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                            <div class="col-3 d-flex align-items-center">
                                                <p>{{getBaseQuantity(item, ItemIndex)}}</p>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="col-1 d-flex align-items-center">
                                            </div>
                                        </template>

                                        <div :class="{ 'col-5': baseInfo.bag_type === 'triple_chamber', 'col-3': baseInfo.bag_type !== 'triple_chamber','d-flex align-items-center': baseInfo.label !== 'Primene'  }">
                                            <div class="d-flex align-items-center" v-if="!item.choices || formData.formulation.electrolytes[ItemIndex].type !== ''">
                                                <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.electrolytes[ItemIndex].extra_quantity" @input="resetStability();" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                                <span class="form-uom">{{item.unit}}</span>
                                            </div>
                                        </div>

                                        <template v-if="baseInfo.label === 'Primene' && item.label === 'Phosphate' && hasOrganicPhosphate()">
                                            <div class="col-4" >
                                                <div v-html="getPrimenePhosphateMessage()" class="extra-message"></div>
                                            </div>
                                        </template>

                                    </div>
                                    <div class="formulation-form-row" v-if="formData.errors.electrolytes['item_' + ItemIndex]" :key="'validation_' + ItemIndex" >
                                        <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                            <div class="col-7"></div>
                                            <div class="col-5 error-col">
                                                <span class="form-error">Outside guidelines for stability</span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="col-5"></div>
                                            <div class="col-5 error-col">
                                                <span class="form-error">Outside guidelines for stability</span>
                                            </div>
                                        </template>
                                    </div>
                                </template>
                            </div>
                            <!-- Chloride + Acetate -->
                            <div class="subpanel" v-if="baseInfo.electrolytes.chloride_acetate">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Chloride + Acetate</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && (formData.formulation.chloride_acetate.label === 'Specified Chloride' || formData.formulation.chloride_acetate.label === 'Specified Acetate')" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4 d-flex align-items-center">
                                        <select class="form-control" :disabled="viewMode" v-model="formData.formulation.chloride_acetate.label" @change="onSelectChlorideAcetateType()">
                                            <option class="d-none" value="">- Select -</option>
                                            <option v-for="(type, typeIndex) in formulationsData.settings.chloride_acetate.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <span class="form-error" v-if="$v.formData.formulation.chloride_acetate.label.$dirty && !$v.formData.formulation.chloride_acetate.label.required">Required field</span>
                                    </div>
                                    <div class="col-3" v-if="formData.formulation.chloride_acetate.label === 'Specified Chloride' || formData.formulation.chloride_acetate.label === 'Specified Acetate'">
                                        <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" v-model="formData.formulation.chloride_acetate.quantity" @keydown="$event.key === '-' ? $event.preventDefault() : null" @input="resetStability();" >
                                        <span class="form-uom">mmol</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="$v.formData.formulation.chloride_acetate.quantity.$dirty && !$v.formData.formulation.chloride_acetate.quantity.isValid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Required field</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Micronutrients -->
                    <div class="inner" v-if="baseInfo.micronutrients">
                        <div class="section-micronutrients">
                            <!-- Micronutrients unit switcher -->
                            <div class="intro">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-7">
                                        <h4>Micronutrients</h4>
                                    </div>
                                    <div class="col-5">
                                        <div class="unit-control-selection d-flex align-items-center">
                                            <span>&micro;g</span>
                                            <div class="unit-control-switch">
                                                <input type="checkbox" id="switch" v-model="formData.micronutrientsUnitToggle" @change="onSwitchUnit" >
                                                <label for="switch">Toggle</label>
                                            </div>
                                            <span>&micro;mol</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Trace Elements -->
                            <div class="subpanel" v-if="baseInfo.micronutrients.trace_elements">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Trace Element</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && formData.formulation.micronutrients.trace_elements[0].name && formData.formulation.micronutrients.trace_elements[0].name !== 'none'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="micronutrients-list type-te">
                                    <div class="micronutrients-item" v-for="(item, index) in formData.formulation.micronutrients.trace_elements" :key="index" >
                                        <MicronutrientTraceElementRow :rowIndex="index" :rowItem="item" :elementsList="baseInfo.micronutrients.trace_elements" :formTraceElements="formData.formulation.micronutrients.trace_elements" :micronutrientsUnit="formData.micronutrientsUnit" :country="formData.country" :settings="formulationsData.settings.trace_elements" :viewMode="viewMode" :error="formData.errors.trace_elements[index]" :errorMulti="formData.errors.trace_elements_multi[index]" :ref="'te_row_' + index" @updateTraceElementTotals="updateTraceElementTotals" @resetStability="resetStability" @resetTEError="resetTEError" @removeTEError="removeTEError" ></MicronutrientTraceElementRow>
                                    </div>
                                    <button v-if="!viewMode" class="list-item-add no-print" v-on:click="addMicronutrientTraceElementRow">Add another trace element</button>
                                </div>
                            </div>
                            <!-- Vitamins -->
                            <div class="subpanel" v-if="baseInfo.micronutrients.vitamins">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Vitamins</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && formData.formulation.micronutrients.vitamins[0].name && formData.formulation.micronutrients.vitamins[0].name !== 'none'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="micronutrients-list type-vitamin">
                                    <div class="micronutrients-item" v-for="(item, index) in formData.formulation.micronutrients.vitamins" :key="index" >
                                        <MicronutrientVitaminRow :rowIndex="index" :rowItem="item" :elementsList="baseInfo.micronutrients.vitamins" :formVitamins="formData.formulation.micronutrients.vitamins" :bagName="baseInfo.label" :viewMode="viewMode" :country="formData.country" :error="formData.errors.vitamins[index]" :selectedLipid="formData.formulation.macronutrients.lipid.label" :ref="'vitamin_row_' + index" @resetStability="resetStability" @removeVitaminError="removeVitaminError" @resetVitaminError="resetVitaminError" ></MicronutrientVitaminRow>
                                        <div class="formulation-form-row" v-if="$v.formData.formulation.micronutrients.vitamins.$each[index].$dirty && !$v.formData.formulation.micronutrients.vitamins.$each[index].isValid">
                                            <div class="col-12">
                                                <span class="form-error" >Vitalipid not compatible with fish lipid</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button v-if="!viewMode" class="list-item-add no-print" v-on:click="addMicronutrientVitaminRow">Add another vitamin</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Additives -->
                    <div class="inner" v-if="baseInfo.additives">
                        <div class="section-additives">
                            <div class="formulation-form-row align-items-center">
                                <div class="col-4">
                                    <h4>Additives</h4>
                                </div>
                                <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                    <h5 class="label">Total per litre</h5>
                                </div>
                            </div>
                            <template v-for="(item, ItemIndex) in baseInfo.additives.items">
                                <div class="formulation-form-row" :key="ItemIndex">
                                    <div class="col-4 d-flex align-items-center">
                                        <p>{{item.label}}</p>
                                    </div>
                                    <div class="col-3 d-flex align-items-center">
                                        <input :disabled="viewMode" type="number" min="0" step="any" class="form-control" @input="resetStability()" v-model="formData.formulation.additives[ItemIndex].quantity" @keydown="$event.key === '-' ? $event.preventDefault() : null">
                                        <span class="form-uom">{{item.unit}}</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.additives['item_' + ItemIndex]" :key="'validation_additives_' + ItemIndex" >
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="$v.formData.formulation.additives.$each[ItemIndex].$dirty && !$v.formData.formulation.additives.$each[ItemIndex].isValid" :key="'validation_additives_valid_' + ItemIndex">
                                    <div class="col-12">
                                        <span class="form-error" >Heparin not compatible with Soluvit</span>
                                    </div>
                                </div>
                            </template>
                            <div v-if="baseInfo.additives.fluids" class="fluids-notice">
                                <p>If you would like to add fluid to your formulation, please <a href="mailto:contact@baxter.com.au">contact Baxter</a></p>
                            </div>
                        </div>
                    </div>
                    <!-- comments -->
                    <div class="inner">
                        <div class="section-comments">
                            <h4>Comments</h4>
                            <div class="formulation-form-row">
                                <div class="col-9 d-flex align-items-center">
                                    <textarea  :disabled="viewMode" class="form-control" v-model="formData.comments"></textarea>
                                    <span class="form-error" v-if="$v.formData.comments.$dirty && !$v.formData.comments.maxLength">Max 1000 characters</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="link-back">
                    <button @click.prevent="onModalDiscardOpen()">&lt; Back to Custom Formulations</button>
                </div>
            </div>
            <div class="col-4 print-full">
                <div class="recap-col">
                    <RecapTable :formData="formData" :baseInfo="baseInfo" :settings="formulationsData.settings" :showComments="false" :ref="'recap_table'" />
                    <StabilityPanel :stability="formData.stability" />
                    <div v-if="!viewMode" class="navigation-container  no-print">
                        <button type="button" class="bax-btn bax-btn-bordered-blue-brand" :class="{ 'loading': draftSaving }" @click.prevent="onSaveDraft">Save Draft</button>
                        <button v-if="formData.stability !== 'stable' && formData.stability !== 'unstable'" type="button" class="bax-btn bax-btn-blue-brand" :class="{ 'loading': checkingStability }" @click.prevent="onCheckStability">Check Stability</button>
                        <button v-if="formData.stability === 'stable' || formData.stability === 'unstable'" type="button" class="bax-btn bax-btn-blue-brand" @click.prevent="onModalSubmitReviewOpen()">Submit for review</button>
                    </div>
                    <div class="feedback" v-if="feedbackMessage">
                        <p v-html="feedbackMessage"></p>
                    </div>
                </div>
            </div>
            <ChangeBagModal class="change-bag-modal" :open="modalChangeBag" @modal_change_bag_close="onModalChangeBagClose" />
            <SubmitReviewModal class="submit-review-modal" :open="modalSubmitReview" :formData="formData" :savedId="savedId" @modal_submit_review_close="onModalSubmitReviewClose" @modal_submit_review_submit="onSubmitForReview" />
            <DeleteDraftModal class="delete-draft-modal" :open="modalDeleteDraft" :savedId="savedId" @modal_delete_draft_close="onModalDeleteDraftClose()" />
            <CopyFormulationModal class="copy-formulation-modal" :open="modalCopyFormulation" @modal_copy_formulation="onModalCopyFormulation" />
            <DiscardModal class="discard-modal" :open="modalDiscard" @modal_discard="onModalDiscardClose()" />
        </div>
    </div>
    <div v-else>
        <p>Sorry, something went wrong</p>
    </div>
</div>
</template>
<script>

// import { required, requiredIf } from 'vuelidate/lib/validators';
import tpnUtilsMixin from '@/mixins/tpnUtilsMixin';
import MicronutrientTraceElementRow from '@/views/Tpn/CustomFormulationCreate/MicronutrientTraceElementRow.vue';
import MicronutrientVitaminRow from '@/views/Tpn/CustomFormulationCreate/MicronutrientVitaminRow.vue';
import RecapTable from '@/views/Tpn/CustomFormulationCreate/RecapTable.vue';
import StabilityPanel from '@/views/Tpn/CustomFormulationCreate/StabilityPanel.vue';
import CustomFormulationService from '@/core/api/tpn/customFormulation.service';
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators';
import { scrollPageToFirstError } from '@/core/helpers';
import ChangeBagModal from '@/views/Tpn/CustomFormulationCreate/ChangeBagModal.vue';
import SubmitReviewModal from '@/views/Tpn/CustomFormulationCreate/SubmitReviewModal.vue';
import DeleteDraftModal from '@/views/Tpn/CustomFormulationCreate/DeleteDraftModal.vue';
import CopyFormulationModal from '@/views/Tpn/CustomFormulationCreate/CopyFormulationModal.vue';
import SavedPanel from '@/views/Tpn/CustomFormulationCreate/SavedPanel.vue';
import DiscardModal from '@/views/Tpn/CustomFormulationCreate/DiscardModal.vue';

const greaterThanZero = (value) => {
    if (!value || parseFloat(value) === 0) {
        return false;
    }
    return true;
}


export default {
    name: 'FormStep3',
    mixins: [tpnUtilsMixin],
    components: {
        MicronutrientTraceElementRow,
        MicronutrientVitaminRow,
        RecapTable,
        StabilityPanel,
        ChangeBagModal,
        SubmitReviewModal,
        DeleteDraftModal,
        CopyFormulationModal,
        DiscardModal,
        SavedPanel,
    },
    props: {
        formData: {
            required: true,
        },
        formulationRecordId: {
            required: true,
        },
        formulationsData: {
            required: true,
        },
        preloadedFormData: {
            required: true,
        },
        viewMode: {
            required: true,
        },
    },
    data() {
        return {
            baseInfo: this.formulationsData.bases[this.formData.base],
            savedId: this.formulationRecordId,
            draftSaving: false,
            checkingStability: false,
            feedbackMessage: false,
            validForm: true,
            modalDeleteDraft: false,
            modalCopyFormulation: false,
            modalChangeBag: false,
            modalSubmitReview: false,
            modalDiscard: false,
        };
    },
    validations: {
        formData: {
            bag_details: {
                required,
            },
            comments: {
                maxLength: maxLength(1000),
            },
            bag_volume: {
                isValid: greaterThanZero
            },
            formulation: {
                macronutrients: {
                    amino_acid: {
                        quantity: {
                            isValid(value) {
                                if (this.baseInfo.macronutrients && this.baseInfo.macronutrients.amino_acid) {
                                    return greaterThanZero(value);
                                }
                                return true;
                            }
                        }
                    },
                    glucose: {
                        quantity_g: {
                            isValid(value) {
                                if (this.baseInfo.macronutrients && this.baseInfo.macronutrients.glucose) {
                                    return greaterThanZero(value);
                                }
                                return true;
                            }
                        }
                    },
                    lipid: {
                        label: {
                            required: requiredIf(function () {
                                if (this.baseInfo.macronutrients && this.baseInfo.macronutrients.lipid) {
                                    return true;
                                }
                                return false;
                            }),
                        },
                        quantity_g: {
                            isValid(value) {
                                if (this.formData.formulation.macronutrients.lipid.label && this.baseInfo.macronutrients && this.baseInfo.macronutrients.lipid) {
                                    return greaterThanZero(value);
                                }
                                return true;
                            }
                        }
                    },
                },
                electrolytes: {
                    $each: {
                        type: {
                            required: requiredIf(function (el) {
                                if (el.label === 'Phosphate' && this.baseInfo.label === 'Primene') {
                                    return true;
                                }
                                return false;
                            }),
                        },
                    },
                },
                micronutrients: {
                    vitamins: {
                        $each: {
                            isValid(value) {
                                if ((value.name === 'Vitalipid' || value.name === 'Vitalipid Infant') && this.formData.formulation.macronutrients.lipid.label === 'Fish lipid') {
                                    return false;
                                }
                                return true;
                            }
                        }
                    }
                },
                chloride_acetate: {
                    label: {
                        required: requiredIf(function () {
                            if (this.baseInfo.electrolytes.chloride_acetate) {
                                return true;
                            }
                            return false;
                        }),
                    },
                    quantity: {
                        required: requiredIf(function () {
                            if (this.formData.formulation.chloride_acetate.label === 'Specified Chloride' || this.formData.formulation.chloride_acetate.label === 'Specified Acetate') {
                                return true;
                            }
                            return false;
                        }),
                        isValid(value) {
                            if (this.formData.formulation.chloride_acetate.label === 'Specified Chloride' || this.formData.formulation.chloride_acetate.label === 'Specified Acetate') {
                                return greaterThanZero(value);
                            }
                            return true;
                        }
                    },
                },
                additives: {
                    $each: {
                        isValid(value) {
                            // special case for primene - no heparine and soluvit together
                            if (this.baseInfo.label === 'Primene' && value.label === 'Heparin' && value.quantity > 0) {
                                let SoluvitFound = false;
                                this.formData.formulation.micronutrients.vitamins.forEach((item) => {
                                    if (item.name === 'Soluvit' && item.value > 0) {
                                        SoluvitFound = true;
                                    }
                                });
                                if (SoluvitFound) {
                                    return false;
                                }
                            }
                            return true;
                        }
                    }
                },
            },
        },
    },
    created() {
        if (!this.preloadedFormData) {
            this.baseInfo = this.formulationsData.bases[this.formData.base];
            if (this.baseInfo) {
                this.formData.bag_volume = this.baseInfo.bag_volume;

                if (this.baseInfo.macronutrients) {
                    if (this.baseInfo.macronutrients.amino_acid) {
                        const aminoAcidBase = this.baseInfo.macronutrients.amino_acid;
                        // handle macronutrients - amino acids
                        if (aminoAcidBase.choices.length === 1) {
                            const selectedItem = this.formulationsData.settings.amino_acid.find((item) => item.label === aminoAcidBase.choices[0]);
                            this.formData.selectedAminoAcidType = selectedItem;
                            this.formData.formulation.macronutrients.amino_acid.label = selectedItem.label;
                            // this.formData.formulation.macronutrients.amino_acid.nitrogen = this.formData.selectedAminoAcidType.nitrogen;
                        }
                    }

                    if (this.baseInfo.macronutrients.glucose) {
                        const glucoseBase = this.baseInfo.macronutrients.glucose;
                        // handle macronutrients - glucose
                        if (glucoseBase.choices.length === 1) {
                            const selectedItem = this.formulationsData.settings.glucose.find((item) => item.label === glucoseBase.choices[0]);
                            this.formData.selectedGlucoseType = selectedItem;
                            this.formData.formulation.macronutrients.glucose.label = selectedItem.label;
                        }
                    }

                    if (this.baseInfo.macronutrients.lipid) {
                        const lipidBase = this.baseInfo.macronutrients.lipid;
                        // handle macronutrients - lipid
                        if (lipidBase.choices.length === 1) {
                            const selectedItem = this.formulationsData.settings.lipid.find((item) => item.label === lipidBase.choices[0]);
                            this.formData.selectedLipidType = selectedItem;
                            this.formData.formulation.macronutrients.lipid.label = selectedItem.label;
                        }
                    }
                }
                if (this.baseInfo.electrolytes) {
                    // add electrolytes to formulation form
                    if (this.baseInfo.electrolytes.items) {
                        this.baseInfo.electrolytes.items.forEach((item) => {
                            const data = {
                                label: item.label,
                                extra_quantity: '',
                            };
                            if (item.choices) {
                                data.type = '';
                                if (item.choices.length === 1) {
                                    data.type = item.choices[0].label;
                                }
                            }
                            this.formData.formulation.electrolytes.push(data);
                        });
                    }
                    // add electrolytes chloride_acetate
                    if (this.baseInfo.electrolytes.chloride_acetate) {
                        this.formData.formulation.chloride_acetate = {
                            label: '',
                            quantity: '',
                        };
                    }
                }

                // add additives to formulation form
                if (this.baseInfo.additives) {
                    if (this.baseInfo.additives.items) {
                        this.baseInfo.additives.items.forEach((item) => {
                            const data = {
                                label: item.label,
                                quantity: '',
                            };
                            this.formData.formulation.additives.push(data);
                        });
                    }
                }
            }
            // add errors
            this.formData.formulation.electrolytes.forEach((item, index) => {
                const keyName = 'item_' + index;
                this.$set(this.formData.errors.electrolytes, keyName, false);
            });
        }
    },
    methods: {
        getPrimenePhosphateMessage() {
            return "<p>Organic Phosphate will provide extra Sodium as 2mmol sodium per mmol Organic Phosphate.<br><strong>Please check total Sodium required carefully</strong></p>";
        },
        hasOrganicPhosphate() {
            let found = false;
            this.formData.formulation.electrolytes.forEach((item) => {
                if (item.label === 'Phosphate' && item.type === 'Organic' && item.extra_quantity) {
                    found = true;
                }
            });
            return found;
        },
        resetTEError(index) {
            this.formData.errors.trace_elements[index] = false;
            this.formData.errors.trace_elements_multi[index] = false;
        },
        removeTEError(index) {
            this.formData.errors.trace_elements.splice(index, 1);
            this.formData.errors.trace_elements_multi.splice(index, 1);
        },
        resetVitaminError(index) {
            this.formData.errors.vitamins[index] = false;
        },
        removeVitaminError(index) {
            this.formData.errors.vitamins.splice(index, 1);
        },
        getLipidLabel(value) {
            let label = value;
            if (label === 'Omegaven') {
                if (this.formData.country === 'Australia') {
                    label += ' [SAS]';
                }
            }
            return label;
        },
        getBaseQuantity(item, ItemIndex) {
            let value = 0;
            if (item.choices) {
                if (item.choices.length > 1) {
                    const selectedItem = item.choices.find((el) => el.label === this.formData.formulation.electrolytes[ItemIndex].type);
                    if (selectedItem) {
                        value = selectedItem.base_quantity;
                    }
                } else {
                    value = item.choices[0].base_quantity;
                }
            } else {
                value = item.base_quantity;
            }
            return this.formatNumber(value, 2) + ' ' + item.unit;
        },
        onModalChangeBagOpen() {
            this.modalChangeBag = true;
        },
        onModalChangeBagClose(reset) {
            if (reset) {
                this.$emit('change_bag');
            }
            this.modalChangeBag = false;
        },
        onModalSubmitReviewOpen() {
            this.modalSubmitReview = true;
        },
        onModalSubmitReviewClose() {
            this.modalSubmitReview = false;
        },
        onModalDeleteDraftOpen() {
            this.modalDeleteDraft = true;
        },
        onModalDeleteDraftClose() {
            this.modalDeleteDraft = false;
        },
        onModalDiscardOpen() {
            this.modalDiscard = true;
        },
        onModalDiscardClose() {
            this.modalDiscard = false;
        },
        onModalCopyFormulationOpen() {
            this.validateForm();
            if (this.validForm) {
                this.modalCopyFormulation = true;
            }
        },
        onModalCopyFormulation(copy) {
            if (copy) {
                // save current copy
                this.validateForm();
                if (this.validForm) {
                    this.draftSaving = true;
                    this.feedbackMessage = false;
                    const payload = {
                        type: 'saveDraft',
                        formData: this.formData,
                        formulationRecordId: this.savedId,
                    };
                    CustomFormulationService.handleCustomFormulation(payload)
                    .then((res) => {
                        this.draftSaving = false;
                        if (res.data.error) {
                            this.feedbackMessage = 'Sorry, something went wrong';
                        } else {
                            // this.feedbackMessage = 'Draft saved successfully,<br>' + res.data.date;
                            // this.savedId = res.data.id;
                            // this.formData.status = 'draft';
                            // this.formData.created_by_user_id = res.data.user_id;
                            // this.formData.created_by_user_name = res.data.user_name;

                            this.savedId = false;
                            this.$emit('set_preloaded_data', this.formData);

                            const path = '/tpn/customFormulationCreate';
                            if (this.$route.path !== path) {
                                this.$router.push({ name: 'TPNCustomFormulationCreate' });
                            }
                            this.modalCopyFormulation = false;
                        }
                    });
                }
            } else {
                this.modalCopyFormulation = false;
            }
        },
        addMicronutrientTraceElementRow() {
            this.formData.formulation.micronutrients.trace_elements.push({ name: '', value: '', unit: '' });
            this.resetStability();
        },
        addMicronutrientVitaminRow() {
            this.formData.formulation.micronutrients.vitamins.push({ name: '', value: '', unit: '' });
            this.resetStability();
        },
        onSwitchUnit() {
            if (this.formData.micronutrientsUnitToggle) {
                this.formData.micronutrientsUnit = 'micromol';
            } else {
                this.formData.micronutrientsUnit = 'microgram';
            }
            // reset values of affected items
            this.formData.formulation.micronutrients.trace_elements.forEach((item, index) => {
                if (item.type === 'single') {
                    this.formData.formulation.micronutrients.trace_elements[index].value = '';
                }
            });

            this.resetStability();
            this.updateTraceElementTotals();
        },
        onSelectElectrolyteType(event, index) {
            // const { value } = event.target;
            // search selected element
            // if (!value || value === 'none') {
                this.formData.formulation.electrolytes[index].extra_quantity = '';
            // }
            this.resetStability();
        },
        onSelectChlorideAcetateType() {
            this.formData.formulation.chloride_acetate.quantity = '';
            this.resetStability();
        },
        onSelectAminoAcidType(event) {
            const { value } = event.target;
            // search selected element
            const selectedItem = this.formulationsData.settings.amino_acid.find((item) => item.label === value);

            // handle composition
            if (selectedItem) {
                this.formData.selectedAminoAcidType = selectedItem;
                if (this.formData.formulation.macronutrients.amino_acid.quantity) {
                    this.formData.formulation.macronutrients.amino_acid.nitrogen = (this.formData.formulation.macronutrients.amino_acid.quantity * selectedItem.nitrogen) / selectedItem.g_l;
                } else {
                    this.formData.formulation.macronutrients.amino_acid.nitrogen = '';
                }
            } else {
                this.formData.selectedAminoAcidType = false;
                this.formData.formulation.macronutrients.amino_acid.nitrogen = '';
                this.formData.formulation.macronutrients.amino_acid.quantity = '';
            }
            this.resetStability();
        },
        onInputAminoacidValue(event) {
            const { value } = event.target;
            if (value) {
                this.formData.formulation.macronutrients.amino_acid.nitrogen = (value * this.formData.selectedAminoAcidType.nitrogen) / this.formData.selectedAminoAcidType.g_l;
            } else {
                this.formData.formulation.macronutrients.amino_acid.nitrogen = '';
            }
            this.resetStability();
        },
        onSelectGlucoseType(event) {
            const { value } = event.target;
            // search selected element
            const selectedItem = this.formulationsData.settings.glucose.find((item) => item.label === value);

            // handle composition
            if (selectedItem) {
                this.formData.selectedGlucoseType = selectedItem;
                this.formData.formulation.macronutrients.glucose.quantity_g = '';
                this.formData.formulation.macronutrients.glucose.quantity_kcal = '';
            } else {
                this.formData.selectedGlucoseType = false;
                this.formData.formulation.macronutrients.glucose.quantity_g = '';
                this.formData.formulation.macronutrients.glucose.quantity_kcal = '';
            }
            this.resetStability();
        },
        onInputGlucoseValue(event, unit) {
            const { value } = event.target;
            if (unit === 'g') {
                const conversion = this.convertGlucose('g', 'kcal', value);
                if (conversion) {
                    this.formData.formulation.macronutrients.glucose.quantity_kcal = this.formatNumber(conversion, 2);
                } else {
                    this.formData.formulation.macronutrients.glucose.quantity_kcal = '';
                }
            }
            if (unit === 'kcal') {
                const conversion = this.convertGlucose('kcal', 'g', value);
                if (conversion) {
                    this.formData.formulation.macronutrients.glucose.quantity_g = this.formatNumber(conversion, 2);
                } else {
                    this.formData.formulation.macronutrients.glucose.quantity_g = '';
                }
            }
            this.resetStability();
        },
        onSelectLipidType(event) {
            const { value } = event.target;
            // search selected element
            const selectedItem = this.formulationsData.settings.lipid.find((item) => item.label === value);

            // handle composition
            if (selectedItem) {
                this.formData.selectedLipidType = selectedItem;
                this.formData.formulation.macronutrients.lipid.quantity_g = '';
                this.formData.formulation.macronutrients.lipid.quantity_kcal = '';
            } else {
                this.formData.selectedLipidType = false;
                this.formData.formulation.macronutrients.lipid.quantity_g = '';
                this.formData.formulation.macronutrients.lipid.quantity_kcal = '';
            }
            this.formData.errors.lipids = false;
            this.resetStability();
        },
        onInputLipidValue(event, unit) {
            const { value } = event.target;
            const selectedLipid = this.formulationsData.settings.lipid.find((el) => el.label === this.formData.formulation.macronutrients.lipid.label);
            if (unit === 'g') {
                const conversion = this.convertLipid('g', 'kcal', selectedLipid.g_to_kcal_rate, value);
                if (conversion) {
                    this.formData.formulation.macronutrients.lipid.quantity_kcal = this.formatNumber(conversion, 2);
                } else {
                    this.formData.formulation.macronutrients.lipid.quantity_kcal = '';
                }
            }
            if (unit === 'kcal') {
                const conversion = this.convertLipid('kcal', 'g', selectedLipid.g_to_kcal_rate, value);
                if (conversion) {
                    this.formData.formulation.macronutrients.lipid.quantity_g = this.formatNumber(conversion, 2);
                } else {
                    this.formData.formulation.macronutrients.lipid.quantity_g = '';
                }
            }
            this.resetStability();
        },
        updateTraceElementTotals() {
            // loop through trace Elements to get totals
            const list = [];
            this.formData.formulation.micronutrients.trace_elements.forEach((item) => {
                if (this.validNumber(item.value)) {
                    if (item.type === 'single') {
                        const index = list.findIndex((listItem) => listItem.name === item.name);

                        if (index >= 0) {
                            // i founds the element, add qty
                            list[index].value += parseFloat(item.value);
                        } else {
                            // element not found, add it
                            const data = {
                                name: item.name,
                                value: parseFloat(item.value),
                            };
                            list.push(data);
                        }
                    }

                    if (item.type === 'multi') {
                        const selectedItem = this.formulationsData.settings.trace_elements.find((el) => el.label === item.name);
                        if (selectedItem) {
                            selectedItem.composition.forEach((te) => {
                                const index = list.findIndex((listItem) => listItem.name === te.label);

                                if (index >= 0) {
                                    // i founds the element, add qty
                                    list[index].value += te[this.formData.micronutrientsUnit] * item.value;
                                } else {
                                    // element not found, add it
                                    const data = {
                                        name: te.label,
                                        value: te[this.formData.micronutrientsUnit] * item.value,
                                    };
                                    list.push(data);
                                }
                            });
                        }
                    }
                }
            });

            // add extra zinc if exists
            if (this.baseInfo.default_list_data.extra_zinc) {
                const index = list.findIndex((listItem) => listItem.name === 'Zinc');
                if (index >= 0) {
                    // i founds the element, add qty
                    list[index].value += this.baseInfo.default_list_data.extra_zinc[this.formData.micronutrientsUnit];
                }
            }

            this.formData.traceElementRecapTotals = list;
        },
        onSaveDraft() {
            if (!this.draftSaving && !this.checkingStability) {
                this.validateForm();
                if (this.validForm) {
                    this.draftSaving = true;
                    this.feedbackMessage = false;
                    const payload = {
                        type: 'saveDraft',
                        formData: this.formData,
                        formulationRecordId: this.savedId,
                    };
                    CustomFormulationService.handleCustomFormulation(payload)
                    .then((res) => {
                        this.draftSaving = false;
                        if (res.data.error) {
                            this.feedbackMessage = 'Sorry, something went wrong';
                        } else {
                            this.feedbackMessage = 'Draft saved successfully,<br>' + res.data.date;
                            this.savedId = res.data.id;
                            this.formData.status = 'draft';
                            this.formData.created_by_user_id = res.data.user_id;
                            this.formData.created_by_user_name = res.data.user_name;
                        }
                    });
                }
            }
        },
        onCheckStability() {
            if (!this.draftSaving && !this.checkingStability) {
                this.validateForm();
                if (this.validForm) {
                    this.checkingStability = true;
                    this.feedbackMessage = false;
                    const payload = {
                        type: 'checkStability',
                        formData: this.formData,
                        formulationRecordId: this.savedId,
                    };
                    CustomFormulationService.handleCustomFormulation(payload)
                    .then((res) => {
                        this.checkingStability = false;
                        if (res.data.stability.error || res.data.save.error) {
                            this.feedbackMessage = 'Sorry, something went wrong';
                        } else {
                            this.formData.errors = res.data.stability.stabilityErrors;
                            this.formData.stability = res.data.stability.isStable;
                            this.feedbackMessage = 'Draft saved successfully,<br>' + res.data.save.date;
                            this.savedId = res.data.save.id;
                            this.formData.status = 'draft';
                            this.formData.created_by_user_id = res.data.save.user_id;
                            this.formData.created_by_user_name = res.data.save.user_name;
                        }
                        this.$nextTick(() => scrollPageToFirstError('form-error'));
                    });
                }
            }
        },
        onSubmitForReview() {
            this.modalSubmitReview = false;
            this.formData.status = 'submitted';
            this.$emit('validation_response', !this.$v.$invalid);
        },
        validateForm() {
            this.validForm = true;

            // default validation
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.validForm = false;
                this.$nextTick(() => scrollPageToFirstError('form-error'));
            }
        },
        resetStability() {
            this.formData.stability = '';
        },
        onPrint() {
            window.print();
        },
    },
};
</script>
